import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api.js';
import { Button } from '@/components/ui/button.jsx';

// R39 (NIGHT-13, David 2026-11-02): every bank account has an export slot, and one drop zone takes any of their exports.
// The server works out the account from the file (its name, or its bank's own shape) and never asks; the answer is
// "N lines added to <account> through <date>" or exactly why not. This screen derives nothing: the slots, the account and
// the refusal words are the server's (services/money/bank-exports.service.js, routes/money.routes.js exportRefusal).

const NAMED_BY = {
  number: 'by the account number in the file name',
  only: 'it is the bank’s only account',
  only_trust: 'when nothing in the file names another account and the file follows on from what is already loaded',
  mirror: 'by its transfers: two or more of them mirror lines already loaded for the bank\u2019s other account',
  cannot: null,
};
const INFERRED = {
  stated: ' (you picked the account)',
  mirror: ' (its transfers mirror lines already in the bank\u2019s other account)',
  number: ' (the file name gives the account number)',
  overlap: ' (some of its lines were already in this account)',
  continuity: ' (it follows on from this account’s last balance)',
  only: '',
  only_trust: '',
};

function resultWords(file, r) {
  const into = `${r.account?.name || 'the account'}${INFERRED[r.inferred_by] || ''}`;
  if (r.inserted) return `${file}: ${r.inserted} line${r.inserted === 1 ? '' : 's'} added to ${into}${r.through ? ` through ${r.through}` : ''}${r.duplicates ? `; ${r.duplicates} already there` : ''}.`;
  return `${file}: nothing new. All ${r.duplicates} line${r.duplicates === 1 ? ' was' : 's were'} already in ${into}${r.through ? ` (through ${r.through})` : ''}.`;
}

export default function BankExports({ onLoaded }) {
  const [slots, setSlots] = useState(null);
  const [slotError, setSlotError] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const picker = useRef(null);

  const refresh = useCallback(async () => {
    try { setSlots((await api.get('/api/money/bank-exports/slots')).slots || []); setSlotError(''); }
    catch (e) { setSlotError(e.message || 'Could not read the export slots'); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  // One file, posted once. A refusal that offers the bank's accounts (the server could not tell whose it is) keeps the
  // file so it can be posted again, once, naming the account the broker picks.
  const post = async (f, accountId = null) => {
    try {
      const r = await api.post('/api/money/bank-exports', { filename: f.name, csv_text: await f.text(), ...(accountId ? { account_id: accountId } : {}) });
      return { ok: true, text: resultWords(f.name, r) };
    } catch (e) {
      return { ok: false, text: `${f.name}: ${e.body?.error || e.message || 'could not be read'}`, file: f, pick: Array.isArray(e.body?.pick) ? e.body.pick : [] };
    }
  };
  const after = async (out) => {
    setBusy(false);
    await refresh();
    if (onLoaded && out.some((x) => x.ok)) await onLoaded();
  };
  const load = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setBusy(true);
    const out = [];
    for (const f of list) out.push(await post(f));
    setResults(out);
    await after(out);
  };
  const pickFor = async (i, accountId) => {
    setBusy(true);
    const r = await post(results[i].file, accountId);
    const out = results.map((x, j) => (j === i ? { ...r, pick: [] } : x));
    setResults(out);
    await after(out);
  };

  return (
    <div className="mt-6 rounded-card border border-border bg-surface p-4">
      <h3 className="text-sm font-semibold text-text">Bank exports</h3>
      <p className="mt-1 text-xs text-text-muted">Drop any CSV a bank gave you. TRACON works out which account it is from the file and loads its lines on the spot.</p>
      <div
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); load(e.dataTransfer.files); }}
        className={`mt-3 flex flex-col items-center justify-center gap-2 rounded-card border-2 border-dashed p-6 text-sm ${over ? 'border-accent bg-bg' : 'border-border'}`}
      >
        <span className="text-text-muted">{busy ? 'Loading…' : 'Drop the export here'}</span>
        <input ref={picker} type="file" accept=".csv,text/csv" multiple className="hidden" onChange={(e) => { load(e.target.files); e.target.value = ''; }} />
        <Button onClick={() => picker.current && picker.current.click()} disabled={busy}>Choose a file</Button>
      </div>
      {results.length ? (
        <ul className="mt-3 space-y-1 text-sm">
          {results.map((x, i) => (
            <li key={i} className={x.ok ? 'text-text' : 'text-danger'}>
              {x.text}
              {!x.ok && x.pick && x.pick.length ? (
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-text-muted">
                  <span>If you know whose export it is, say so once for this file:</span>
                  {x.pick.map((a) => <Button key={a.id} size="sm" variant="outline" onClick={() => pickFor(i, a.id)} disabled={busy}>It is {a.name}&apos;s</Button>)}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {slotError ? <p className="mt-3 text-sm text-danger">{slotError}</p> : null}
      {slots ? (
        <ul className="mt-3 divide-y divide-border text-sm">
          {slots.map((s) => {
            return (
              <li key={s.account.id} className="py-1.5">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-text">{s.account.name}</span>
                  <span className={s.never_loaded || s.stale ? 'text-danger' : 'text-text-muted'}>
                    {s.never_loaded ? 'never loaded' : `lines through ${s.through}${s.stale ? ` (${s.age_days} days old)` : ''}, loaded ${String(s.loaded_at || '').slice(0, 10)}`}
                  </span>
                </div>
                <div className={`text-xs ${s.named_by === 'cannot' ? 'text-danger' : 'text-text-dim'}`}>{NAMED_BY[s.named_by] ? `Recognised ${NAMED_BY[s.named_by]}.` : 'Not recognised yet: it has no account number on file, and nothing else in a file says it is this account’s. A file for it is refused, never loaded into another account.'}</div>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
